import { parseCidr } from '../../apps/cidr-planner/cidrUtils';
import { getSubnetColor } from '../../apps/cidr-planner/subnetColors';
import { isAwsForbiddenSubnet, usableIpCount } from '../../apps/cidr-planner/usableIpUtils';
import type { CidrBlock, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import './SubnetList.css';

type SubnetListProps = {
  blocks: CidrBlock[];
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  actionLabel: string;
  onAction: () => void;
  onSelect: (block: CidrBlock) => void;
  onDelete: (blockId: string) => void;
};

const TEXT = {
  ja: {
    title: 'ブロック一覧',
    empty: 'ブロックがありません',
    usable: '使用可能 IP',
    awsForbidden: 'AWS VPC では不可',
    delete: '削除',
  },
  en: {
    title: 'Blocks',
    empty: 'No blocks yet',
    usable: 'Usable IPs',
    awsForbidden: 'Not allowed in AWS VPC',
    delete: 'Delete',
  },
} as const;

export function SubnetList({
  blocks,
  ipCountMode,
  lang,
  actionLabel,
  onAction,
  onSelect,
  onDelete,
}: SubnetListProps) {
  const t = TEXT[lang];
  const sorted = [...blocks].sort((a, b) => {
    const aStart = parseCidr(a.cidr).network;
    const bStart = parseCidr(b.cidr).network;
    if (aStart < bStart) return -1;
    if (aStart > bStart) return 1;
    return 0;
  });

  return (
    <section className="cidr-subnet-list" aria-label={t.title}>
      <div className="cidr-subnet-list__header">
        <h2 className="cidr-subnet-list__title">{t.title}</h2>
        <button type="button" className="cidr-subnet-list__add-btn" onClick={onAction}>
          {actionLabel}
        </button>
      </div>
      {sorted.length === 0 ? (
        <p className="cidr-subnet-list__empty">{t.empty}</p>
      ) : (
        <ul className="cidr-subnet-list__items">
          {sorted.map((block) => {
            const prefix = parseCidr(block.cidr).prefix;
            const usable = usableIpCount(prefix, ipCountMode);
            const forbidden = isAwsForbiddenSubnet(prefix, ipCountMode);
            return (
              <li key={block.id} className="cidr-subnet-list__item">
                <button
                  type="button"
                  className="cidr-subnet-list__main"
                  onClick={() => onSelect(block)}
                >
                  <span
                    className="cidr-subnet-list__swatch"
                    style={{ backgroundColor: getSubnetColor(prefix) }}
                    aria-hidden
                  />
                  <span className="cidr-subnet-list__body">
                    <span className="cidr-subnet-list__cidr">{block.cidr}</span>
                    {block.name && (
                      <span className="cidr-subnet-list__name">{block.name}</span>
                    )}
                    <span className="cidr-subnet-list__meta">
                      {t.usable}: {usable}
                      {forbidden && (
                        <span className="cidr-subnet-list__warn"> ({t.awsForbidden})</span>
                      )}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="cidr-subnet-list__delete"
                  onClick={() => onDelete(block.id)}
                  aria-label={`${t.delete} ${block.cidr}`}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
